import { ActivityLog, LogType, LogAction, LogTargetType, LogResult } from '../types';
import { PocketBaseDataService as DataService } from './pocketbaseDataService';

interface LogParams {
  log_type: LogType;
  actor_id: string;
  actor_name: string;
  actor_role: 'global_admin' | 'partner_admin' | 'system';
  partner_id?: string;
  target_type: LogTargetType;
  target_id?: string;
  target_name?: string;
  action: LogAction;
  description: string;
  result: LogResult;
  error_message?: string;
  metadata?: Record<string, any>;
}

class LoggingService {
  /**
   * Create a new activity log entry
   */
  async createLog(params: LogParams): Promise<void> {
    try {
      const logEntry: Omit<ActivityLog, 'id' | 'created'> = {
        ...params,
        ip_address: this.getClientIP(),
        user_agent: navigator.userAgent,
      };

      await DataService.createActivityLog(logEntry);
    } catch (error) {
      console.error('Failed to create activity log:', error);
      // Don't throw - logging failures shouldn't break the app
    }
  }

  /**
   * Log user login
   */
  async logLogin(userId: string, userName: string, role: 'global_admin' | 'partner_admin', partnerId?: string): Promise<void> {
    await this.createLog({
      log_type: 'user_action',
      actor_id: userId,
      actor_name: userName,
      actor_role: role,
      partner_id: partnerId,
      target_type: 'user',
      target_id: userId,
      target_name: userName,
      action: 'login',
      description: `User ${userName} logged in`,
      result: 'success',
    });
  }

  /**
   * Log profile updates (e.g., name changes)
   */
  async logProfileUpdate(
    userId: string,
    userName: string,
    role: 'global_admin' | 'partner_admin',
    partnerId: string | undefined,
    changes: Record<string, any>
  ): Promise<void> {
    await this.createLog({
      log_type: 'user_action',
      actor_id: userId,
      actor_name: userName,
      actor_role: role,
      partner_id: partnerId,
      target_type: 'user',
      target_id: userId,
      target_name: userName,
      action: 'profile_update',
      description: `Updated profile fields: ${Object.keys(changes).join(', ')}`,
      result: 'success',
      metadata: changes,
    });
  }

  /**
   * Log partner company profile change requests
   */
  async logCompanyProfileChangeRequest(
    userId: string,
    userName: string,
    role: 'global_admin' | 'partner_admin',
    partnerId: string,
    partnerName: string,
    requestDetails: string
  ): Promise<void> {
    await this.createLog({
      log_type: 'partner_action',
      actor_id: userId,
      actor_name: userName,
      actor_role: role,
      partner_id: partnerId,
      target_type: 'partner',
      target_id: partnerId,
      target_name: partnerName,
      action: 'profile_update',
      description: `Requested profile changes for ${partnerName}`,
      result: 'success',
      metadata: { details: requestDetails },
    });
  }

  /**
   * Log user logout
   */
  async logLogout(userId: string, userName: string, role: 'global_admin' | 'partner_admin', partnerId?: string): Promise<void> {
    await this.createLog({
      log_type: 'user_action',
      actor_id: userId,
      actor_name: userName,
      actor_role: role,
      partner_id: partnerId,
      target_type: 'user',
      target_id: userId,
      target_name: userName,
      action: 'logout',
      description: `User ${userName} logged out`,
      result: 'success',
    });
  }

  /**
   * Log password change
   */
  async logPasswordChange(userId: string, userName: string, role: 'global_admin' | 'partner_admin', partnerId?: string, success: boolean = true): Promise<void> {
    await this.createLog({
      log_type: 'user_action',
      actor_id: userId,
      actor_name: userName,
      actor_role: role,
      partner_id: partnerId,
      target_type: 'user',
      target_id: userId,
      target_name: userName,
      action: 'password_change',
      description: success ? `User ${userName} changed their password` : `Failed password change attempt for ${userName}`,
      result: success ? 'success' : 'failure',
    });
  }

  /**
   * Log advertisement creation
   */
  async logAdvertisementCreated(
    actorId: string,
    actorName: string,
    actorRole: 'global_admin' | 'partner_admin',
    partnerId: string,
    adId: string,
    adName: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.createLog({
      log_type: 'advertisement_action',
      actor_id: actorId,
      actor_name: actorName,
      actor_role: actorRole,
      partner_id: partnerId,
      target_type: 'advertisement',
      target_id: adId,
      target_name: adName,
      action: 'created',
      description: `Created advertisement "${adName}"`,
      result: 'success',
      metadata,
    });
  }

  /**
   * Log advertisement update
   */
  async logAdvertisementUpdated(
    actorId: string,
    actorName: string,
    actorRole: 'global_admin' | 'partner_admin',
    partnerId: string,
    adId: string,
    adName: string,
    changes?: Record<string, any>
  ): Promise<void> {
    await this.createLog({
      log_type: 'advertisement_action',
      actor_id: actorId,
      actor_name: actorName,
      actor_role: actorRole,
      partner_id: partnerId,
      target_type: 'advertisement',
      target_id: adId,
      target_name: adName,
      action: 'updated',
      description: `Updated advertisement "${adName}"`,
      result: 'success',
      metadata: changes,
    });
  }

  /**
   * Log advertisement deletion
   */
  async logAdvertisementDeleted(
    actorId: string,
    actorName: string,
    actorRole: 'global_admin' | 'partner_admin',
    partnerId: string,
    adId: string,
    adName: string
  ): Promise<void> {
    await this.createLog({
      log_type: 'advertisement_action',
      actor_id: actorId,
      actor_name: actorName,
      actor_role: actorRole,
      partner_id: partnerId,
      target_type: 'advertisement',
      target_id: adId,
      target_name: adName,
      action: 'deleted',
      description: `Deleted advertisement "${adName}"`,
      result: 'success',
    });
  }

  /**
   * Log advertisement approval
   */
  async logAdvertisementApproved(
    actorId: string,
    actorName: string,
    partnerId: string,
    adId: string,
    adName: string
  ): Promise<void> {
    await this.createLog({
      log_type: 'advertisement_action',
      actor_id: actorId,
      actor_name: actorName,
      actor_role: 'global_admin',
      partner_id: partnerId,
      target_type: 'advertisement',
      target_id: adId,
      target_name: adName,
      action: 'approved',
      description: `Approved advertisement "${adName}"`,
      result: 'success',
      metadata: { previous_status: 'pending', new_status: 'approved' },
    });
  }

  /**
   * Log advertisement rejection
   */
  async logAdvertisementRejected(
    actorId: string,
    actorName: string,
    partnerId: string,
    adId: string,
    adName: string,
    reason?: string
  ): Promise<void> {
    await this.createLog({
      log_type: 'advertisement_action',
      actor_id: actorId,
      actor_name: actorName,
      actor_role: 'global_admin',
      partner_id: partnerId,
      target_type: 'advertisement',
      target_id: adId,
      target_name: adName,
      action: 'rejected',
      description: `Rejected advertisement "${adName}"${reason ? `: ${reason}` : ''}`,
      result: 'success',
      metadata: { previous_status: 'pending', new_status: 'rejected', rejection_reason: reason },
    });
  }

  /**
   * Log campaign creation
   */
  async logCampaignCreated(
    actorId: string,
    actorName: string,
    actorRole: 'global_admin' | 'partner_admin',
    partnerId: string | undefined,
    campaignId: string,
    campaignName: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.createLog({
      log_type: 'campaign_action',
      actor_id: actorId,
      actor_name: actorName,
      actor_role: actorRole,
      partner_id: partnerId,
      target_type: 'campaign',
      target_id: campaignId,
      target_name: campaignName,
      action: 'created',
      description: `Created campaign "${campaignName}"`,
      result: 'success',
      metadata,
    });
  }

  /**
   * Log campaign update
   */
  async logCampaignUpdated(
    actorId: string,
    actorName: string,
    actorRole: 'global_admin' | 'partner_admin',
    partnerId: string | undefined,
    campaignId: string,
    campaignName: string,
    changes?: Record<string, any>
  ): Promise<void> {
    await this.createLog({
      log_type: 'campaign_action',
      actor_id: actorId,
      actor_name: actorName,
      actor_role: actorRole,
      partner_id: partnerId,
      target_type: 'campaign',
      target_id: campaignId,
      target_name: campaignName,
      action: 'updated',
      description: `Updated campaign "${campaignName}"`,
      result: 'success',
      metadata: changes,
    });
  }

  /**
   * Log campaign status change
   */
  async logCampaignStatusChanged(
    actorId: string,
    actorName: string,
    actorRole: 'global_admin' | 'partner_admin',
    partnerId: string | undefined,
    campaignId: string,
    campaignName: string,
    oldStatus: string,
    newStatus: string
  ): Promise<void> {
    await this.createLog({
      log_type: 'campaign_action',
      actor_id: actorId,
      actor_name: actorName,
      actor_role: actorRole,
      partner_id: partnerId,
      target_type: 'campaign',
      target_id: campaignId,
      target_name: campaignName,
      action: 'status_changed',
      description: `Changed campaign "${campaignName}" status from "${oldStatus}" to "${newStatus}"`,
      result: 'success',
      metadata: { old_status: oldStatus, new_status: newStatus },
    });
  }

  /**
   * Log promo request status change
   */
  async logPromoRequestStatusChanged(
    actorId: string,
    actorName: string,
    actorRole: 'global_admin' | 'partner_admin',
    partnerId: string,
    requestId: string,
    userEmail: string,
    oldStatus: string,
    newStatus: string
  ): Promise<void> {
    await this.createLog({
      log_type: 'promo_request_action',
      actor_id: actorId,
      actor_name: actorName,
      actor_role: actorRole,
      partner_id: partnerId,
      target_type: 'promo_request',
      target_id: requestId,
      target_name: userEmail,
      action: 'status_changed',
      description: `Changed promo request status for ${userEmail} from "${oldStatus}" to "${newStatus}"`,
      result: 'success',
      metadata: { old_status: oldStatus, new_status: newStatus },
    });
  }

  /**
   * Log failed login attempt
   */
  async logFailedLogin(
    email: string,
    attemptNumber: number,
    remainingAttempts: number,
    userId?: string,
    userName?: string,
    partnerId?: string
  ): Promise<void> {
    await this.createLog({
      log_type: 'user_action',
      actor_id: userId || 'unknown',
      actor_name: userName || email,
      actor_role: 'system',
      partner_id: partnerId,
      target_type: 'user',
      target_id: userId,
      target_name: email,
      action: 'failed_login',
      description: `Failed login attempt ${attemptNumber}/5 for ${email}. ${remainingAttempts} attempts remaining.`,
      result: 'failure',
      metadata: {
        email,
        attempt_number: attemptNumber,
        remaining_attempts: remainingAttempts,
        account_exists: !!userId
      },
    });
  }

  /**
   * Log account lockout
   */
  async logAccountLockout(
    email: string,
    lockoutDurationMinutes: number,
    userId?: string,
    userName?: string,
    partnerId?: string
  ): Promise<void> {
    await this.createLog({
      log_type: 'user_action',
      actor_id: userId || 'unknown',
      actor_name: userName || email,
      actor_role: 'system',
      partner_id: partnerId,
      target_type: 'user',
      target_id: userId,
      target_name: email,
      action: 'lockout',
      description: `Account locked for ${email} due to too many failed login attempts. Lockout duration: ${lockoutDurationMinutes} minutes.`,
      result: 'failure',
      error_message: `Account temporarily locked for ${lockoutDurationMinutes} minutes`,
      metadata: {
        email,
        lockout_duration_minutes: lockoutDurationMinutes,
        account_exists: !!userId,
        security_event: 'brute_force_protection'
      },
    });
  }

  /**
   * Log blocked login attempt during lockout
   */
  async logBlockedLoginAttempt(
    email: string,
    remainingLockoutMinutes: number,
    userId?: string,
    userName?: string,
    partnerId?: string
  ): Promise<void> {
    await this.createLog({
      log_type: 'user_action',
      actor_id: userId || 'unknown',
      actor_name: userName || email,
      actor_role: 'system',
      partner_id: partnerId,
      target_type: 'user',
      target_id: userId,
      target_name: email,
      action: 'lockout_attempt',
      description: `Login attempt blocked for ${email} - account is locked. ${remainingLockoutMinutes} minutes remaining.`,
      result: 'failure',
      error_message: `Account locked - ${remainingLockoutMinutes} minutes remaining`,
      metadata: {
        email,
        remaining_lockout_minutes: remainingLockoutMinutes,
        account_exists: !!userId,
        security_event: 'lockout_active'
      },
    });
  }

  /**
   * Get client IP address (best effort)
   */
  private getClientIP(): string {
    // In a real app, this would come from the server
    // For now, return placeholder
    return 'client';
  }
}

export const loggingService = new LoggingService();
